import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, ArrowUpRight, CheckCheck } from 'lucide-react';
import { useLoneStarAuth } from '@/context/LoneStarAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Cross-brand notification feed — shows safe, PII-free summaries of new
 * Amigo's Bail Bonds website submissions (leads + full intake
 * applications). Viewing/acknowledging here never touches the underlying
 * Amigos lead/submission workflow state — it only affects this
 * notification's own viewed/acknowledged/dismissed state.
 */
const LoneStarOverview = () => {
  const { token } = useLoneStarAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=30`, authHeader);
      setItems(res.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const acknowledge = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/acknowledge`, {}, authHeader);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, acknowledged_at: new Date().toISOString(), viewed_at: n.viewed_at || new Date().toISOString() } : n)));
    } catch { /* non-critical */ }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div data-testid="lonestar-overview">
      <div className="dash-panel__head">
        <h2>{"Amigo's Bail Bonds \u2014 Incoming Activity"}</h2>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.86rem', marginBottom: '1.2rem', maxWidth: '65ch' }}>
        {"Safe, summary-only alerts for new website submissions from the Amigo's Bail Bonds public site. "
          + 'No defendant names, charges, bond amounts, or contact details are shared here \u2014 open the Amigos '
          + 'dashboard directly for full case details.'}
      </p>

      <div className="dash-panel">
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-dim)' }}>Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty-state" data-testid="lonestar-empty-state">
            <Bell size={30} />
            <div>{"No Amigo's Bail Bonds activity yet."}</div>
          </div>
        ) : (
          items.map((n) => (
            <div key={n.id} className="audit-row" data-testid={`lonestar-notification-${n.id}`}>
              <div className="audit-row__time">{timeAgo(n.created_at)}</div>
              <div className="audit-row__body" style={{ flex: 1 }}>
                <b>{n.title}</b> — {n.summary}
                {n.priority === 'urgent' && <span className="status-pill status-Urgent" style={{ marginLeft: '0.6rem' }}>Urgent</span>}
              </div>
              {!n.acknowledged_at ? (
                <button className="dash-btn" style={{ fontSize: '0.75rem' }} onClick={() => acknowledge(n.id)} data-testid={`lonestar-ack-btn-${n.id}`}>
                  <CheckCheck size={13} /> Acknowledge
                </button>
              ) : (
                <span style={{ fontSize: '0.75rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><CheckCheck size={13} /> Acknowledged</span>
              )}
            </div>
          ))
        )}
      </div>

      <a href={process.env.REACT_APP_BACKEND_URL?.replace('/api', '') + '/dashboard/login'} target="_self" rel="noreferrer"
         style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: 'var(--gold)' }} data-testid="lonestar-back-to-amigos-link">
        <ArrowUpRight size={14} /> Go to Amigos dashboard for full case details
      </a>
    </div>
  );
};

export default LoneStarOverview;
